import { useEffect, useMemo, useRef } from 'react'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { popupForPage, popupsFromUis } from 'lib/labV2/popups'
import { snackbarForPage, snackbarsFromUis, type LabV2Snackbar } from 'lib/labV2/snackbars'
import { isLabV2ModalSuppressed } from 'storage/labV2Modal'
import { isLabV2SnackbarSuppressed, recordLabV2SnackbarSeen } from 'storage/labV2Snackbar'

export function presentLabV2Snackbar(snackbar: LabV2Snackbar) {
  recordLabV2SnackbarSeen(snackbar)
  showSnackbar(snackbar.description, snackbar.kind, { heading: snackbar.heading })
}

export default function useLabV2Snackbar({ enabled }: { enabled: boolean }) {
  const { pathname, currentRegionCode } = useLabRouteRequestContext()
  const uis = useAppSelector(state => state.system.features.labV2?.uis)
  const snackbars = useMemo(() => snackbarsFromUis(uis), [uis])
  const popups = useMemo(() => popupsFromUis(uis), [uis])
  const shownKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) {
      return
    }
    const snackbar = snackbarForPage(snackbars, pathname, currentRegionCode)
    if (!snackbar || isLabV2SnackbarSuppressed(snackbar)) {
      return
    }
    const shownKey = `${snackbar.id}:${pathname}`
    if (shownKeyRef.current === shownKey) {
      return
    }

    const popup = popupForPage(popups, pathname, currentRegionCode)
    if (popup && !isLabV2ModalSuppressed(popup)) {
      return
    }

    shownKeyRef.current = shownKey
    presentLabV2Snackbar(snackbar)
  }, [currentRegionCode, enabled, pathname, popups, snackbars])
}
